import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Mail, Send, Sparkles, Loader2, Plus, Trash2, Image as ImageIcon,
  Eye, ExternalLink, X, CalendarClock, Pencil, Check, Link2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  formatInTz, localInputToUtcIso, propertyTimezone, tzAbbreviation,
  utcIsoToLocalInput,
} from "@/lib/timezone";
import RecommendedArticlesPanel from "@/components/RecommendedArticlesPanel";
import NewsletterHistoryPanel from "@/components/NewsletterHistoryPanel";
import PublishOSReport from "@/components/PublishOSReport";
import PublishOSClientPicker from "@/components/PublishOSClientPicker";
import { anyReportable } from "@/lib/publishosView";
import {
  alreadyIn, effectiveDisplay, entryForCandidate, entryFromLink, isLinkEntry,
  withEditedCopy,
  keepValid, patchEntry,
} from "@/lib/newsletterEntries";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Newsletter builder — one screen, three sections:
 *   1. Article picker (with 7-day engagement badges from FB)
 *   2. Advertiser slot picker (0-3 ads from the library)
 *   3. Live preview + "Create draft in MailerLite"
 *
 * State is kept local. On "Create draft" we push to the backend
 * which builds the HTML, saves a `newsletters` doc, AND creates a
 * draft campaign in MailerLite that the editor finalises + sends
 * from ML's dashboard. Send-from-Newsdesk is on the roadmap.
 */
export default function NewsletterPage() {
  const qc = useQueryClient();
  const [entries, setEntries] = useState([]); // {article_id, headline, wordpress_url, featured_image_url, teaser}
  const [selectedAdIds, setSelectedAdIds] = useState([]);
  const [showAdMgr, setShowAdMgr] = useState(false);
  const [teasingIds, setTeasingIds] = useState(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Admin-only: create the MailerLite draft/campaign for real, but
  // don't let it mark its articles as used. For testing the newsletter
  // end to end without dirtying the picker. Server-enforced.
  const [countUsage, setCountUsage] = useState(true);
  // Composed payload from the AI. When set, the preview modal is open
  // and Create-draft / Send buttons submit exactly this data (no
  // double-generation, no double-cost).
  const [composed, setComposed] = useState(null);

  const { data: ads = [] } = useQuery({
    queryKey: ["newsletter-ads"],
    queryFn: () => api.listNewsletterAds(),
  });
  // Newsroom timezone: schedule times are typed and shown in it.
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.getSettings(),
  });
  const tz = propertyTimezone(settings);
  // PublishOS link (billing). When on, a sent newsletter's ads become
  // bookings there; `billing` holds a result the editor must act on and
  // `draftAsk` is the "create bookings for this draft?" question.
  const { data: publishos } = useQuery({
    queryKey: ["publishos-status"],
    queryFn: () => api.publishosStatus(),
    staleTime: 5 * 60_000,
  });
  const publishosOn = !!publishos?.enabled;
  const [billing, setBilling] = useState(null); // {id, subject, report}
  const [draftAsk, setDraftAsk] = useState(false);
  const afterPublishOS = (r) => {
    const report = r?.publishos;
    if (!report) return;
    if (report.state === "needs_decision" || report.state === "failed") {
      setBilling({ id: r.id, subject: r.subject, report });
    } else if (report.state === "pending") {
      toast.message(tr("Its ads will be reported to PublishOS once it has gone out."));
    } else if (report.summary) {
      toast.message(report.summary);
    }
  };

  // "Do not report to PublishOS", under each ad. Saved on the advertiser
  // straight away and read when the report goes out.
  const skipReport = useMutation({
    mutationFn: ({ id, skip }) =>
      api.patchNewsletterAd(id, { publishos_skip: skip }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["newsletter-ads"] }),
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Could not save that")),
  });

  const selectedIds = new Set(entries.map((e) => e.article_id));

  // When the panel's candidate list changes (e.g. the range dropdown
  // changed), drop any picked article that's no longer in the list.
  // Per user spec: "keep any selected articles still present and drop
  // the rest".
  // "Add from link" entries never come from the picker, so they stay.
  // keepValid returns the same array when nothing dropped, so a
  // background refetch doesn't spam re-renders.
  const onCandidatesChange = (candidates) => {
    setEntries((prev) => keepValid(prev, candidates));
  };

  // The theme decides whether the first entry is featured by default.
  const leadStory = settings?.newsletter_theme_effective?.lead_story !== false;
  const updateEntry = (id, patch) => setEntries((prev) => patchEntry(prev, id, patch));
  const addLinkEntry = (entry, display) => {
    if (alreadyIn(entries, entry)) {
      toast.message(tr("That post is already in this newsletter."));
      return false;
    }
    setEntries((prev) => [...prev, entryFromLink(entry, display)]);
    return true;
  };

  const addArticle = async (c) => {
    if (selectedIds.has(c.article_id)) return;
    // Add first with the WP excerpt as the teaser starter; then auto-
    // generate a fresh AI teaser in the background so the editor
    // doesn't have to click "Generate teaser" for every row. Digital
    // editions go in featured with no teaser, so they skip the AI.
    const stub = entryForCandidate(c);
    setEntries((prev) => [...prev, stub]);
    if (c.kind === "edition") return;
    setTeasingIds((prev) => new Set(prev).add(c.article_id));
    try {
      const r = await api.generateTeaser(c.article_id);
      if (r.teaser) {
        setEntries((prev) =>
          prev.map((e) =>
            e.article_id === c.article_id ? { ...e, teaser: r.teaser } : e,
          ),
        );
      }
    } catch (e) {
      // Non-fatal — the WP excerpt stub stays in place.
      toast.message(
        tr("Teaser auto-gen failed — you can rewrite it inline: {detail}", { detail: e?.response?.data?.detail || e.message }),
      );
    } finally {
      setTeasingIds((prev) => {
        const next = new Set(prev);
        next.delete(c.article_id);
        return next;
      });
    }
  };

  const removeArticle = (id) =>
    setEntries((prev) => prev.filter((e) => e.article_id !== id));

  const updateTeaser = (id, teaser) =>
    setEntries((prev) =>
      prev.map((e) => (e.article_id === id ? { ...e, teaser } : e)),
    );

  const move = (id, dir) => {
    setEntries((prev) => {
      const i = prev.findIndex((e) => e.article_id === id);
      if (i < 0) return prev;
      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= prev.length) return prev;
      const copy = [...prev];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  const toggleAd = (id) => {
    setSelectedAdIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) {
        toast.message(tr("Max 3 ads per newsletter"));
        return prev;
      }
      return [...prev, id];
    });
  };

  const composeInput = () => ({
    articles: entries,
    ads: selectedAdIds
      .map((id) => ads.find((a) => a.id === id))
      .filter(Boolean)
      .map((a) => ({
        ad_id: a.id,
        image_url: a.image_url,
        link_url: a.link_url,
        name: a.name,
      })),
  });

  const composeMut = useMutation({
    mutationFn: () => api.composeNewsletter(composeInput()),
    onSuccess: (r) => {
      setComposed(r);
      setPreviewOpen(true);
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Preview failed")),
  });

  const draftMut = useMutation({
    mutationFn: (publishosBookings) =>
      api.createNewsletterDraft({
        ...withEditedCopy(composed),
        count_usage: countUsage,
        publishos_bookings: publishosBookings === true,
      }),
    onSuccess: (r) => {
      toast.success(
        tr("Draft created in MailerLite — open ML to review or send."),
      );
      afterPublishOS(r);
      qc.invalidateQueries({ queryKey: ["newsletters"] });
      // Refresh the picker so the "sent/drafted" badges appear.
      qc.invalidateQueries({ queryKey: ["newsletter-candidates"] });
      setPreviewOpen(false);
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Draft creation failed")),
  });

  const sendMut = useMutation({
    mutationFn: () =>
      api.sendNewsletter({ ...withEditedCopy(composed), count_usage: countUsage }),
    onSuccess: (r) => {
      toast.success(tr("Newsletter sent via MailerLite!"));
      afterPublishOS(r);
      qc.invalidateQueries({ queryKey: ["newsletters"] });
      qc.invalidateQueries({ queryKey: ["newsletter-candidates"] });
      setPreviewOpen(false);
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Send failed")),
  });

  const scheduleMut = useMutation({
    mutationFn: (scheduledLocal) =>
      api.scheduleNewsletter({
        ...withEditedCopy(composed),
        count_usage: countUsage,
        scheduled_local: scheduledLocal,
      }),
    onSuccess: (r) => {
      toast.success(
        tr("Scheduled in MailerLite for {when}.", { when: formatInTz(r.scheduled_for, tz) }),
      );
      afterPublishOS(r);
      qc.invalidateQueries({ queryKey: ["newsletters"] });
      qc.invalidateQueries({ queryKey: ["newsletter-candidates"] });
      setPreviewOpen(false);
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || tr("Scheduling failed"));
      // A failed schedule can still leave a draft behind (recorded in
      // History), so refresh it.
      qc.invalidateQueries({ queryKey: ["newsletters"] });
    },
  });

  const canPreview = entries.length > 0 && !composeMut.isPending;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-6 lg:p-10">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-6 h-6 text-[var(--accent)]" />
          <h1 className="font-serif-display text-3xl">{tr("Newsletter")}</h1>
        </div>

        {/* Candidate articles */}
        <RecommendedArticlesPanel
          selectedIds={selectedIds}
          onAdd={addArticle}
          onRemove={removeArticle}
          onCandidatesChange={onCandidatesChange}
        />

        {/* Posts from this site that Newsdesk didn't write (a digital
            edition posted by hand, a promotions flyer…). */}
        <AddFromLink onAdd={addLinkEntry} />

        {/* Selected articles order + teasers */}
        {entries.length > 0 && (
          <div className="bg-white border border-[var(--border)] p-5 mb-6">
            <h2 className="font-serif-display text-xl mb-3">{tr("Articles in this newsletter ({entrieCount})", { entrieCount: entries.length })}</h2>
            <ol className="space-y-3">
              {entries.map((e, i) => (
                <li
                  key={e.article_id}
                  className="border border-[var(--border)] p-3"
                  data-testid={`newsletter-entry-${e.article_id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="text-xs font-mono-display text-[var(--ink-muted)] pt-1">
                      {i + 1}.
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[var(--ink)] font-semibold mb-2 flex items-center gap-2 flex-wrap">
                        <span>{e.headline}</span>
                        {isLinkEntry(e) && (
                          <span className="text-[10px] uppercase tracking-wider font-mono-display px-1.5 py-0.5 border border-[var(--border)] text-[var(--ink-muted)] font-normal">{tr("From link")}</span>
                        )}
                      </div>
                      <EntryStyle
                        entry={e}
                        display={effectiveDisplay(entries, i, leadStory)}
                        onChange={(patch) => updateEntry(e.article_id, patch)}
                      />
                      <textarea
                        data-testid={`newsletter-teaser-${e.article_id}`}
                        value={e.teaser}
                        onChange={(ev) =>
                          updateTeaser(e.article_id, ev.target.value)
                        }
                        rows={2}
                        placeholder={tr("Short click-through teaser")}
                        className="w-full px-2 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
                      />
                      {teasingIds.has(e.article_id) && (
                        <div className="mt-1 text-[11px] text-[var(--ink-muted)] flex items-center gap-1">
                          <Sparkles className="w-3 h-3 animate-pulse" />{tr("Generating fresh teaser…")}</div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => move(e.article_id, "up")}
                        disabled={i === 0}
                        className="text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-30 text-xs"
                        title={tr("Move up")}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        onClick={() => move(e.article_id, "down")}
                        disabled={i === entries.length - 1}
                        className="text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-30 text-xs"
                        title={tr("Move down")}
                      >
                        ▼
                      </button>
                      <button
                        type="button"
                        onClick={() => removeArticle(e.article_id)}
                        className="text-[var(--ink-muted)] hover:text-red-600"
                        title={tr("Remove from newsletter")}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Advertiser slots */}
        <div className="bg-white border border-[var(--border)] p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif-display text-xl">{tr("Advertisers ({selectedAdIdCount}/3)", { selectedAdIdCount: selectedAdIds.length })}</h2>
            <button
              type="button"
              onClick={() => setShowAdMgr((v) => !v)}
              data-testid="newsletter-ad-mgr-toggle"
              className="text-xs font-mono-display uppercase tracking-wider text-[var(--accent)] hover:underline"
            >
              {showAdMgr ? tr("Close") : tr("Manage library")}
            </button>
          </div>
          {ads.length === 0 && (
            <div className="text-sm text-[var(--ink-muted)] py-2">{tr("No advertisers yet — click Manage library to add one.")}</div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {ads.map((a) => {
              const on = selectedAdIds.includes(a.id);
              return (
                <div key={a.id} className="flex flex-col min-w-0">
                  <button
                    type="button"
                    onClick={() => toggleAd(a.id)}
                    data-testid={`newsletter-ad-${a.id}`}
                    className={`text-left border p-2 transition ${
                      on
                        ? "border-[var(--accent)] ring-1 ring-[var(--accent)]"
                        : "border-[var(--border)] hover:border-[var(--border-strong)]"
                    }`}
                  >
                    <div className="aspect-[16/9] bg-[var(--secondary)] mb-2 flex items-center justify-center overflow-hidden">
                      {a.image_url ? (
                        <img
                          src={a.image_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-[var(--ink-muted)]" />
                      )}
                    </div>
                    <div className="text-xs text-[var(--ink)] truncate">
                      {a.name}
                    </div>
                  </button>
                  {publishosOn && (
                    <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[var(--ink-muted)] cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`newsletter-ad-skip-${a.id}`}
                        checked={!!a.publishos_skip}
                        disabled={skipReport.isPending}
                        onChange={(e) =>
                          skipReport.mutate({ id: a.id, skip: e.target.checked })
                        }
                      />{tr("Do not report to PublishOS")}</label>
                  )}
                </div>
              );
            })}
          </div>
          {showAdMgr && (
            <NewsletterAdManager qc={qc} ads={ads} publishos={publishos} />
          )}
        </div>

        {/* What already went out — subject + date, expandable to the
            articles and advertisers each one carried. */}
        <NewsletterHistoryPanel />

        {/* Actions — only Preview. Draft + Send live inside the modal
            so the editor sees exactly what will be sent before choosing. */}
        <div className="flex flex-wrap gap-2 items-center justify-end">
          <button
            type="button"
            data-testid="newsletter-preview"
            onClick={() => composeMut.mutate()}
            disabled={!canPreview}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--accent)] text-white text-sm hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {composeMut.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />{tr("Composing…")}</>
            ) : (
              <>
                <Eye className="w-4 h-4" />{tr("Preview")}</>
            )}
          </button>
        </div>
      </div>

      {previewOpen && composed && (
        <PreviewModal
          composed={composed}
          onCopyChange={(patch) => setComposed((c) => ({ ...c, ...patch }))}
          onClose={() => setPreviewOpen(false)}
          onCreateDraft={() => {
            // Linked to PublishOS and carrying an ad that gets reported
            // (and not an admin test run): ask whether the draft should
            // create bookings there.
            if (publishosOn && countUsage && anyReportable(composed.ads, ads)) {
              setDraftAsk(true);
            } else {
              draftMut.mutate(false);
            }
          }}
          onSend={() => {
            if (
              window.confirm(
                tr("Send this newsletter now via MailerLite? This cannot be undone."),
              )
            ) {
              sendMut.mutate();
            }
          }}
          onSchedule={(local) => scheduleMut.mutate(local)}
          draftBusy={draftMut.isPending}
          sendBusy={sendMut.isPending}
          scheduleBusy={scheduleMut.isPending}
          tz={tz}
          isAdmin={isAdmin}
          countUsage={countUsage}
          onCountUsageChange={setCountUsage}
        />
      )}
      {draftAsk && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setDraftAsk(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            data-testid="newsletter-draft-publishos-dialog"
            className="bg-white w-full max-w-md border border-[var(--border)] p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif-display text-lg text-[var(--ink)] mb-2">{tr("Create bookings in PublishOS?")}</h3>
            <p className="text-xs text-[var(--ink-muted)] mb-4">{tr("A draft has no send date yet. Choosing yes makes sure each linked advertiser has a booking in PublishOS: an unscheduled one is created only for clients that have none. Nothing is marked as sent.")}</p>
            <div className="space-y-2">
              <button
                type="button"
                data-testid="newsletter-draft-publishos-yes"
                onClick={() => {
                  setDraftAsk(false);
                  draftMut.mutate(true);
                }}
                className="w-full text-left px-3 py-2 border border-[var(--border-strong)] hover:bg-[var(--paper-hover)] text-sm text-[var(--ink)] font-semibold"
              >{tr("Yes, create the draft and the bookings")}</button>
              <button
                type="button"
                data-testid="newsletter-draft-publishos-no"
                onClick={() => {
                  setDraftAsk(false);
                  draftMut.mutate(false);
                }}
                className="w-full text-left px-3 py-2 border border-[var(--border-strong)] hover:bg-[var(--paper-hover)] text-sm text-[var(--ink)] font-semibold"
              >{tr("No, just the draft")}</button>
              <button
                type="button"
                data-testid="newsletter-draft-publishos-cancel"
                onClick={() => setDraftAsk(false)}
                className="w-full px-3 py-2 text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]"
              >{tr("Cancel")}</button>
            </div>
          </div>
        </div>
      )}
      {billing && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setBilling(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            data-testid="newsletter-publishos-dialog"
            className="bg-white w-full max-w-lg border border-[var(--border)] p-5 max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-1">
              <h3 className="font-serif-display text-lg text-[var(--ink)]">
                {billing.report?.mode === "draft"
                  ? tr("The draft was created")
                  : tr("The newsletter was sent")}
              </h3>
              <button
                type="button"
                onClick={() => setBilling(null)}
                className="text-[var(--ink-muted)] hover:text-[var(--ink)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-[var(--ink-muted)] mb-3">{tr("Billing in PublishOS needs a moment of your attention. You can also do this later from History.")}</p>
            <PublishOSReport
              newsletterId={billing.id}
              report={billing.report}
              onChange={(report) => setBilling((b) => (b ? { ...b, report } : b))}
            />
            <div className="flex justify-end mt-4">
              <button
                type="button"
                data-testid="newsletter-publishos-dialog-close"
                onClick={() => setBilling(null)}
                className="px-4 py-2 border border-[var(--border-strong)] text-sm hover:bg-[var(--secondary)]"
              >{tr("Done")}</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

/** Default schedule time: the next full hour at least an hour away,
 * as a datetime-local string in the newsroom timezone. */
/**
 * Featured (big image, like the lead story) or Regular (compact row),
 * plus the optional heading line and the byline toggle. `display` is
 * the effective style, so the first entry shows Featured until changed.
 */
function EntryStyle({ entry, display, onChange }) {
  const featured = display === "featured";
  return (
    <div className="mb-2 flex items-center gap-3 flex-wrap text-xs" data-testid={`newsletter-style-${entry.article_id}`}>
      <div className="inline-flex border border-[var(--border)]">
        {["featured", "regular"].map((d) => (
          <button
            key={d}
            type="button"
            data-testid={`newsletter-style-${entry.article_id}-${d}`}
            onClick={() => onChange({ display: d })}
            className={
              "px-2 py-1 " +
              (display === d
                ? "bg-[var(--ink)] text-white"
                : "bg-white text-[var(--ink-muted)] hover:text-[var(--ink)]")
            }
          >
            {d === "featured" ? tr("Featured") : tr("Regular")}
          </button>
        ))}
      </div>
      <label className="inline-flex items-center gap-1.5 text-[var(--ink-muted)] cursor-pointer">
        <input
          type="checkbox"
          checked={entry.show_byline !== false}
          onChange={(ev) => onChange({ show_byline: ev.target.checked })}
        />{tr("Date & author")}</label>
      {featured && (
        <input
          data-testid={`newsletter-heading-${entry.article_id}`}
          value={entry.heading || ""}
          onChange={(ev) => onChange({ heading: ev.target.value })}
          placeholder={tr("Heading line (blank = headline), e.g. New Edition Online. Read it Now!")}
          className="flex-1 min-w-[220px] px-2 py-1 text-xs border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
        />
      )}
    </div>
  );
}

/**
 * "Add from link": paste a link to a post on this paper's own website
 * and add it as a featured or regular entry. The backend looks the post
 * up in WordPress (POST /newsletter/link-entry).
 */
function AddFromLink({ onAdd }) {
  const [url, setUrl] = useState("");
  const [display, setDisplay] = useState("featured");
  const lookup = useMutation({
    mutationFn: () => api.newsletterLinkEntry(url.trim()),
    onSuccess: (entry) => {
      if (onAdd(entry, display)) {
        toast.success(tr("Added “{headline}”", { headline: entry.headline }));
        setUrl("");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Couldn't add that link")),
  });
  return (
    <div className="bg-white border border-[var(--border)] p-4 mb-6" data-testid="newsletter-add-link">
      <div className="overline flex items-center gap-1.5 mb-2">
        <Link2 className="w-3 h-3" />{" "}{tr("Add from link")}</div>
      <form
        className="flex flex-col sm:flex-row gap-2"
        onSubmit={(ev) => {
          ev.preventDefault();
          if (url.trim()) lookup.mutate();
        }}
      >
        <input
          data-testid="newsletter-add-link-url"
          value={url}
          onChange={(ev) => setUrl(ev.target.value)}
          placeholder={tr("Link to a post on this paper's website (digital edition, flyer…)")}
          className="flex-1 px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
        />
        <select
          data-testid="newsletter-add-link-display"
          value={display}
          onChange={(ev) => setDisplay(ev.target.value)}
          className="px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
        >
          <option value="featured">{tr("Featured story")}</option>
          <option value="regular">{tr("Regular story")}</option>
        </select>
        <button
          type="submit"
          data-testid="newsletter-add-link-submit"
          disabled={!url.trim() || lookup.isPending}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-60"
        >
          {lookup.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}{tr("Add")}</button>
      </form>
      <p className="mt-1.5 text-[11px] text-[var(--ink-muted)]">{tr("It goes at the end of the list; move it with the arrows. Featured shows the heading and the full-width image.")}</p>
    </div>
  );
}

function defaultScheduleLocal(tz) {
  const d = new Date(Date.now() + 60 * 60_000);
  d.setUTCMinutes(0, 0, 0);
  d.setTime(d.getTime() + 60 * 60_000);
  return utcIsoToLocalInput(d.toISOString(), tz);
}

function PreviewModal({
  composed, onCopyChange, onClose, onCreateDraft, onSend, onSchedule, draftBusy, sendBusy,
  scheduleBusy, tz, isAdmin, countUsage, onCountUsageChange,
}) {
  const busy = draftBusy || sendBusy || scheduleBusy;
  const noSubject = !(composed.subject || "").trim();
  const copyInputClass =
    "w-full px-2 py-1 text-[var(--ink)] border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)] disabled:opacity-50";
  const [scheduling, setScheduling] = useState(false);
  const [scheduleLocal, setScheduleLocal] = useState(() =>
    defaultScheduleLocal(tz),
  );
  const minLocal = utcIsoToLocalInput(
    new Date(Date.now() + 5 * 60_000).toISOString(), tz,
  );
  const scheduleIso = localInputToUtcIso(scheduleLocal, tz);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-3xl h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-4 py-2 border-b border-[var(--border)]">
          <div className="flex-1 min-w-0 space-y-1.5">
            <label className="block">
              {trRich("<0>Subject</0>", null, [<span className="block font-mono-display text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-0.5" />])}
              <input
                data-testid="newsletter-preview-subject"
                value={composed.subject || ""}
                onChange={(e) => onCopyChange({ subject: e.target.value })}
                disabled={busy}
                className={`${copyInputClass} text-sm font-semibold`}
              />
            </label>
            <label className="block">
              {trRich("<0>Preheader</0>", null, [<span className="block font-mono-display text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-0.5" />])}
              <input
                data-testid="newsletter-preview-preheader"
                value={composed.preheader || ""}
                onChange={(e) => onCopyChange({ preheader: e.target.value })}
                disabled={busy}
                className={`${copyInputClass} text-xs`}
              />
            </label>
          </div>
          <button
            type="button"
            data-testid="newsletter-preview-close"
            onClick={onClose}
            className="text-[var(--ink-muted)] hover:text-[var(--ink)]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <iframe
          data-testid="newsletter-preview-iframe"
          title={tr("Newsletter preview")}
          srcDoc={composed.html}
          className="flex-1 border-0 bg-[var(--bg)]"
        />
        {scheduling && (
          <div
            data-testid="newsletter-schedule-row"
            className="flex flex-wrap gap-2 items-center justify-end px-4 py-3 border-t border-[var(--border)]"
          >
            <label className="text-xs text-[var(--ink-muted)]" htmlFor="newsletter-schedule-at">{tr("Send on")}</label>
            <input
              id="newsletter-schedule-at"
              data-testid="newsletter-schedule-at"
              type="datetime-local"
              value={scheduleLocal}
              min={minLocal}
              onChange={(e) => setScheduleLocal(e.target.value)}
              className="px-3 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
            />
            <span className="text-xs text-[var(--ink-muted)]">
              {tzAbbreviation(tz, scheduleIso ? new Date(scheduleIso) : new Date())}
            </span>
            <button
              type="button"
              data-testid="newsletter-schedule-confirm"
              onClick={() => onSchedule(scheduleLocal)}
              disabled={busy || noSubject || !scheduleLocal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--accent)] text-white text-sm hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {scheduleBusy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CalendarClock className="w-4 h-4" />
              )}{tr("Schedule in MailerLite")}</button>
            <button
              type="button"
              onClick={() => setScheduling(false)}
              disabled={busy}
              className="text-xs text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-50"
            >{tr("Cancel")}</button>
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-center justify-end px-4 py-3 border-t border-[var(--border)]">
          {isAdmin && (
            <label
              className="mr-auto flex items-center gap-2 text-[11px] text-[var(--ink-muted)] cursor-pointer"
              title={tr("Creates the campaign in MailerLite as normal, but leaves the articles unmarked in the picker. Admins only.")}
            >
              <input
                type="checkbox"
                data-testid="newsletter-skip-usage"
                checked={!countUsage}
                onChange={(e) => onCountUsageChange(!e.target.checked)}
              />{tr("Don't mark these articles as used (test run)")}</label>
          )}
          <button
            type="button"
            data-testid="newsletter-create-draft"
            onClick={onCreateDraft}
            disabled={busy || noSubject}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--border-strong)] text-sm hover:bg-[var(--secondary)] disabled:opacity-50"
          >
            {draftBusy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}{tr("Create draft in MailerLite")}</button>
          <button
            type="button"
            data-testid="newsletter-schedule"
            onClick={() => setScheduling((v) => !v)}
            disabled={busy}
            aria-pressed={scheduling}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--border-strong)] text-sm hover:bg-[var(--secondary)] disabled:opacity-50"
          >
            <CalendarClock className="w-4 h-4" />{tr("Schedule…")}</button>
          <button
            type="button"
            data-testid="newsletter-send-now"
            onClick={onSend}
            disabled={busy || noSubject}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--accent)] text-white text-sm hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendBusy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}{tr("Send with MailerLite")}</button>
        </div>
      </div>
    </div>
  );
}

function NewsletterAdManager({ qc, ads, publishos }) {
  const publishosOn = !!publishos?.enabled;
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api.createNewsletterAd({
        name: name.trim(),
        image_url: imageUrl.trim(),
        link_url: linkUrl.trim(),
      }),
    onSuccess: () => {
      setName("");
      setImageUrl("");
      setLinkUrl("");
      qc.invalidateQueries({ queryKey: ["newsletter-ads"] });
      toast.success(tr("Advertiser added"));
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Could not add advertiser")),
  });

  const remove = useMutation({
    mutationFn: (id) => api.deleteNewsletterAd(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["newsletter-ads"] });
    },
  });

  // Edit one stored advertiser in place (name, image, link). `draft`
  // is the row being edited: {id, name, image_url, link_url} or null.
  const [draft, setDraft] = useState(null);
  const startEdit = (a) =>
    setDraft({
      id: a.id,
      name: a.name || "",
      image_url: a.image_url || "",
      link_url: a.link_url || "",
      client: {
        id: a.publishos_client_id || "",
        name: a.publishos_client_name || "",
      },
    });
  const draftValid =
    !!draft &&
    !!draft.name.trim() &&
    !!draft.image_url.trim() &&
    !!draft.link_url.trim();
  const save = useMutation({
    mutationFn: (d) =>
      api.patchNewsletterAd(d.id, {
        name: d.name.trim(),
        image_url: d.image_url.trim(),
        link_url: d.link_url.trim(),
        // Only sent when the link is on, so a property without
        // PublishOS never touches these fields.
        ...(publishosOn
          ? {
              publishos_client_id: d.client?.id || "",
              publishos_client_name: d.client?.name || "",
            }
          : {}),
      }),
    onSuccess: () => {
      setDraft(null);
      qc.invalidateQueries({ queryKey: ["newsletter-ads"] });
      toast.success(tr("Advertiser updated"));
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Could not update advertiser")),
  });
  const editInputClass =
    "px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]";

  return (
    <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-3">
      <h3 className="font-mono-display text-[11px] uppercase tracking-wider text-[var(--ink-muted)]">{tr("Add an advertiser")}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input
          data-testid="newsletter-ad-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={tr("Sponsor name")}
          className="px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
        />
        <input
          data-testid="newsletter-ad-image"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          placeholder={tr("Image URL (JPG/PNG)")}
          className="px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
        />
        <input
          data-testid="newsletter-ad-link"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder={tr("Landing URL")}
          className="px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
        />
      </div>
      <button
        type="button"
        data-testid="newsletter-ad-add"
        onClick={() => create.mutate()}
        disabled={
          !name.trim() || !imageUrl.trim() || !linkUrl.trim() || create.isPending
        }
        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-50"
      >
        {create.isPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Plus className="w-3.5 h-3.5" />
        )}{tr("Add")}</button>
      {ads.length > 0 && (
        <ul className="space-y-1">
          {ads.map((a) =>
            draft?.id === a.id ? (
              <li
                key={a.id}
                data-testid={`newsletter-ad-edit-row-${a.id}`}
                className="border border-[var(--accent)] px-2 py-2 space-y-2"
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    data-testid="newsletter-ad-edit-name"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder={tr("Sponsor name")}
                    className={editInputClass}
                  />
                  <input
                    data-testid="newsletter-ad-edit-image"
                    value={draft.image_url}
                    onChange={(e) =>
                      setDraft({ ...draft, image_url: e.target.value })
                    }
                    placeholder={tr("Image URL (JPG/PNG)")}
                    className={editInputClass}
                  />
                  <input
                    data-testid="newsletter-ad-edit-link"
                    value={draft.link_url}
                    onChange={(e) =>
                      setDraft({ ...draft, link_url: e.target.value })
                    }
                    placeholder={tr("Landing URL")}
                    className={editInputClass}
                  />
                </div>
                {publishosOn && (
                  <PublishOSClientPicker
                    value={draft.client}
                    onChange={(client) => setDraft({ ...draft, client })}
                    propertyName={publishos?.property_name}
                  />
                )}
                <div className="flex items-center gap-2">
                  {draft.image_url.trim() && (
                    <img
                      src={draft.image_url.trim()}
                      alt=""
                      className="h-10 w-auto max-w-[120px] object-contain border border-[var(--border)]"
                    />
                  )}
                  <span className="flex-1" />
                  <button
                    type="button"
                    data-testid="newsletter-ad-edit-cancel"
                    onClick={() => setDraft(null)}
                    disabled={save.isPending}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[var(--border-strong)] hover:bg-[var(--secondary)] disabled:opacity-50"
                  >
                    <X className="w-3.5 h-3.5" />{tr("Cancel")}</button>
                  <button
                    type="button"
                    data-testid="newsletter-ad-edit-save"
                    onClick={() => save.mutate(draft)}
                    disabled={!draftValid || save.isPending}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-50"
                  >
                    {save.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}{tr("Save")}</button>
                </div>
              </li>
            ) : (
              <li
                key={a.id}
                className="flex items-center gap-2 text-xs border border-[var(--border)] px-2 py-1.5"
              >
                <span className="flex-1 truncate">
                  {a.name}
                  {publishosOn && (
                    <span
                      data-testid={`newsletter-ad-publishos-${a.id}`}
                      className={
                        a.publishos_client_id || a.publishos_skip
                          ? "text-[var(--ink-muted)]"
                          : "text-amber-700"
                      }
                    >
                      {a.publishos_skip
                        ? tr(" · Not reported to PublishOS")
                        : a.publishos_client_id
                          ? tr(" · PublishOS: {publishos_client_name}", { publishos_client_name: a.publishos_client_name || tr("linked") })
                          : tr(" · Not linked to PublishOS")}
                    </span>
                  )}
                </span>
                <a
                  href={a.link_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--ink-muted)] hover:text-[var(--accent)]"
                >
                  <ExternalLink className="w-3 h-3" />
                </a>
                <button
                  type="button"
                  data-testid={`newsletter-ad-edit-${a.id}`}
                  onClick={() => startEdit(a)}
                  aria-label={tr("Edit {name}", { name: a.name })}
                  title={tr("Edit name, image or link")}
                  className="text-[var(--ink-muted)] hover:text-[var(--accent)]"
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  data-testid={`newsletter-ad-delete-${a.id}`}
                  onClick={() => remove.mutate(a.id)}
                  className="text-[var(--ink-muted)] hover:text-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}
